var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { HttpEventType, } from '@angular/common/http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import * as Rx from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import * as uuidv4 from 'uuid/v4';
var FileUploadSession = /** @class */ (function () {
    function FileUploadSession(freelancerHttp) {
        this.freelancerHttp = freelancerHttp;
        this._files = [];
        this._filesSubject$ = new Rx.BehaviorSubject(this._files);
        this.files$ = this._filesSubject$.asObservable();
        this._uploads = [];
        this._uploadsSubject$ = new Rx.BehaviorSubject(this._uploads);
        this.uploads$ = this._uploadsSubject$.asObservable();
    }
    /**
     * Add an file to the file list.
     */
    FileUploadSession.prototype.addFile = function (file) {
        // can be undefined due to browser shenanigans
        if (!file) {
            return;
        }
        if (this.containsFile(file)) {
            return;
        }
        var fixedFile = this.fixMatchingFiles(file);
        if (fixedFile !== undefined) {
            this._files = __spreadArrays(this._files, [fixedFile]);
            this._filesSubject$.next(this._files);
        }
    };
    /**
     * Remove an file from the file list.
     */
    FileUploadSession.prototype.removeFile = function (filename) {
        this._files = this._files.filter(function (file) { return file.name !== filename; });
        this._filesSubject$.next(this._files);
    };
    /**
     * Upload files
     */
    FileUploadSession.prototype.send = function (_a) {
        var _this = this;
        var endpoint = _a.endpoint, _b = _a.isGaf, isGaf = _b === void 0 ? false : _b, _c = _a.generateUuid, generateUuid = _c === void 0 ? false : _c, _d = _a.extraParams, extraParams = _d === void 0 ? {} : _d, getId = _a.getId;
        this._files.forEach(function (file) {
            if (file === undefined) {
                return;
            }
            var uploadId = Date.now().toString();
            var uuid = generateUuid ? uuidv4() : undefined;
            var data = new FormData();
            var filename = file.name || undefined;
            data.append('files[]', file, filename);
            Object.entries(extraParams).forEach(function (_a) {
                var key = _a[0], value = _a[1];
                data.append(key, value);
            });
            var response$ = _this.freelancerHttp
                .post(endpoint, data, {
                reportProgress: true,
                isGaf: isGaf,
            })
                .pipe(share());
            var request$ = response$.pipe(filter(function (event) { return event.type === HttpEventType.UploadProgress; }), map(function (event) { return event; }), 
            // Share so we can track when it finishes later in the chain
            // but we can pass it to the upload list for components to
            // observe the upload progress.
            share());
            // As discussed in D118072, this approach goes against modern file upload
            // services, such as S3 and Google Cloud Storage, where generating an
            // ID/key is handled in the application logic before storing the
            // file/object into a bucket. This also prevent features such as resumable
            // file uploads since the file has to be fully uploaded before getting its
            // ID back. As such, this is not the recommended approach. In order to
            // keep us from turning this service into a frankenstein of all File
            // Services, we have to at least map out the current flow of all existing
            // file services in GAF to give us better understanding on what to do next
            // https://phabricator.tools.flnltd.com/T77136.
            var backendId$ = getId
                ? response$.pipe(filter(function (event) { return event.type === HttpEventType.Response; }), map(function (response) {
                    return getId(response);
                }))
                : undefined;
            _this._uploads = __spreadArrays(_this._uploads, [
                {
                    id: uploadId,
                    filename: file.name,
                    uuid: uuid,
                    progress$: request$,
                    failed: false,
                    backendId$: backendId$,
                },
            ]);
            response$
                .toPromise()
                .then(function (response) {
                if (response.type === HttpEventType.Response &&
                    response.body &&
                    response.body.status &&
                    response.body.status !== 'success') {
                    _this.setUploadFailed(uploadId);
                }
                else {
                    _this.removeUpload(uploadId);
                }
            })
                .catch(function () { return _this.setUploadFailed(uploadId); });
        });
        // Empty out files since they're being uploaded right now.
        this._files = [];
        this._uploadsSubject$.next(this._uploads);
        this._filesSubject$.next(this._files);
    };
    /**
     * Remove the upload from the upload list
     */
    FileUploadSession.prototype.removeUpload = function (id) {
        this._uploads = this._uploads.filter(function (upload) { return upload.id !== id; });
        this._uploadsSubject$.next(this._uploads);
    };
    /**
     * Check if there is another file with the same name and same file size
     * already in the file list.
     */
    FileUploadSession.prototype.containsFile = function (file) {
        return (!!this._files &&
            this._files.some(function (f) { return f.name === file.name && f.size === file.size; }));
    };
    /**
     * Fix files being uploaded when there are files in the file list with
     * the same name.
     */
    FileUploadSession.prototype.fixMatchingFiles = function (file) {
        var filename = file.name.split('.');
        var extensions = filename.slice(1).join('.');
        var matchingFiles = this._files
            .filter(function (f) { return f.name.startsWith(filename[0]) && f.name.endsWith(extensions); })
            .sort(function (a, b) { return (a.name < b.name ? -1 : 1); });
        // only modify name if there's a match (also runs when there are no matchingFiles)
        if (matchingFiles.every(function (f) { return f.name !== file.name; })) {
            return file;
        }
        var matchName = matchingFiles[matchingFiles.length - 1].name.split('.');
        var fileNumber = filename[0] !== matchName[0] ? Number(matchName[0].substr(-2, 2)) + 1 : 1;
        var fileNumberPad = "_" + ("0" + fileNumber).slice(-2);
        filename[0] += fileNumberPad;
        return new File([file.slice(0, file.size)], filename.join('.'));
    };
    /**
     * Mark an upload as failed.
     */
    FileUploadSession.prototype.setUploadFailed = function (uploadId) {
        this._uploads = this._uploads.reduce(function (arr, upload) {
            if (upload.id === uploadId) {
                return __spreadArrays(arr, [__assign(__assign({}, upload), { failed: true })]);
            }
            return __spreadArrays(arr, [upload]);
        }, []);
        this._uploadsSubject$.next(this._uploads);
    };
    return FileUploadSession;
}());
export { FileUploadSession };
var FileUploadLegacy = /** @class */ (function () {
    function FileUploadLegacy(freelancerHttp) {
        this.freelancerHttp = freelancerHttp;
    }
    FileUploadLegacy.prototype.createSession = function () {
        return new FileUploadSession(this.freelancerHttp);
    };
    return FileUploadLegacy;
}());
export { FileUploadLegacy };
